// The customizer's browser storage, out of CustomizerScreen (backfill PR 31): the stored
// design read and written with a logged failure instead of a crash (Safari's private mode
// refuses writes), the design the screen starts rendering with (the stored one, migrated to
// the current version, or the default), the first read of the stored design during the
// screen's load, which writes the default when nothing is stored and rewrites a stored
// design the migration moved to a newer version, and the custom-layout notice's list of the
// layouts it was dismissed for. Since chain 8 (Oct 2026) also the default design the load falls
// back to and which of the three places the load reads a design from the address names.
import { validate as uuidValidate } from 'uuid';
import { deepCopy } from './deepObjects';
import { DEFAULT_DESIGN_STATE } from './designState';
import { migrateDesignIfNeeded } from './customizerCompat';

export const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value); // Attempt to store the key-value pair in localStorage
  } catch (error) {
    console.error(`Failed to set localStorage key "${key}":`, error); // Log an error if setting the item fails
  }
}; // Helper function to safely set data in localStorage, ensuring errors are handled gracefully

export const safeLocalStorageGet = (key) => {
  try {
    return localStorage.getItem(key); // Attempt to retrieve the value associated with the key
  } catch (error) {
    console.error(`Failed to get localStorage key "${key}":`, error); // Log an error if retrieval fails
    return null; // Return null to indicate failure, avoiding crashes in the application
  }
}; // Helper function to safely get data from localStorage, ensuring retrieval errors are handled

export const migratedDefaultDesign = () =>
  migrateDesignIfNeeded(deepCopy(DEFAULT_DESIGN_STATE)); // a fresh copy of the default design at the current customizerVersion: what the first render shows when nothing usable is stored, and what the load falls back to when the imported or stored design's layout is gone (CustomizerScreen spelled it out three times until chain 8)

export const loadedDesignSource = (designId) =>
  !designId ? 'storage' : uuidValidate(designId) ? 'cart' : 'database'; // where the load reads the design from, by the address: /customizer reads the browser storage, /customizer/<uuid> the cart item's asset in IndexedDB (the customizer gives every cart item a uuid) and /customizer/<id> a design document from the database (a MongoDB id)

export const storedDesignOrDefault = (key) => {
  const storedValue = safeLocalStorageGet(key); // attempt to retrieve the initial state from localStorage synchronously

  if (storedValue) {
    try {
      return migrateDesignIfNeeded(JSON.parse(storedValue)); // use the stored value if it's valid JSON
    } catch (error) {
      console.error('Failed to parse design state from localStorage:', error);
    }
  }

  return migratedDefaultDesign(); // fallback to the default design state
}; // the design the screen's first render shows (useState's initializer), before its load effect reads the storage again through initializeStorage

export const initializeStorage = (key, defaultValue) => {
  const storedValue = safeLocalStorageGet(key); // Attempt to retrieve the existing value for the given key from localStorage

  if (!storedValue) {
    // if no value exists for the key:
    const preparedDefaultValue = migrateDesignIfNeeded(defaultValue); // migrate the default design state to the latest customizerVersion

    safeLocalStorageSet(key, JSON.stringify(preparedDefaultValue)); // Initialize localStorage with the migrated default value
    return { value: preparedDefaultValue, wasInitialized: true }; // Return the migrated default value and indicate that initialization occurred
  }

  const parsedValue = JSON.parse(storedValue); // parse the existing value from localStorage
  const previousVersion = parsedValue?.metadata?.customizerVersion || '1.0'; // treat designs without a customizerVersion as version 1.0
  const preparedValue = migrateDesignIfNeeded(parsedValue); // migrate the existing design state to the latest customizerVersion
  const nextVersion =
    preparedValue?.metadata?.customizerVersion || previousVersion; // get the version after migration

  if (previousVersion !== nextVersion) {
    // if migrateDesignIfNeeded migrated the design:
    safeLocalStorageSet(key, JSON.stringify(preparedValue)); // save the migrated design state back to localStorage
  }

  return { value: preparedValue, wasInitialized: false }; // Return the migrated value and indicate that no initialization occurred
}; // Helper to initialize or read the design state from localStorage and ensure it uses the latest customizerVersion (a stored value that isn't a design throws here, unlike storedDesignOrDefault: KNOWN_BUGS.md, initializeStorage)

const DISMISSED_CUSTOM_LAYOUT_NOTICES_KEY = 'dismissedCustomLayoutNotices'; // the key inside the users localStorage: an array of layoutIds, for which the notice has already been dismissed in the past

const dismissedCustomLayoutNotices = () => {
  try {
    const stored = JSON.parse(
      safeLocalStorageGet(DISMISSED_CUSTOM_LAYOUT_NOTICES_KEY) || '[]'
    ); // the dismissedCustomLayoutNotices array from the users localStorage
    return Array.isArray(stored) ? stored : [];
  } catch {
    return []; // a stored value that is no JSON: the notice shows again, and the next dismissal starts a new list
  }
}; // the layout ids the notice was dismissed for, an empty list when the storage can't be read or holds something else (the raw read threw inside the screen's effect and blanked the customizer: fixed in PR 31)

export const isCustomLayoutNoticeDismissed = (layoutId) =>
  dismissedCustomLayoutNotices().includes(String(layoutId)); // whether the user has closed the custom-layout notice (save your design regularly) for this layout before

export const dismissCustomLayoutNotice = (layoutId) => {
  const arr = dismissedCustomLayoutNotices();
  const idStr = String(layoutId); // the id of the selected layout
  if (!arr.includes(idStr)) {
    // if the users dismissedCustomLayoutNotices does not include the id of the selected layout:
    arr.push(idStr); // add the id to the dismissedCustomLayoutNotices
    safeLocalStorageSet(
      DISMISSED_CUSTOM_LAYOUT_NOTICES_KEY,
      JSON.stringify(arr)
    ); // update the users localStorage (a refused write is logged, and the screen hides the notice for this visit: it threw out of the close handler before PR 31)
  }
}; // remembers that the user closed the custom-layout notice for this layout
